import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Package, ArrowLeft, RefreshCw, Copy, Check, Trash2, Edit3, AlertTriangle,
  CheckCircle, X, Download, FileText, GitBranch, User as UserIcon, Calendar,
  HardDrive, Hash
} from 'lucide-react';
import useAuthStore from '../stores/authStore';

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return '—';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / 1024 / 1024).toFixed(1) + ' MB';
  return (bytes / 1024 / 1024 / 1024).toFixed(2) + ' GB';
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit'
  });
}

const PUSH_STATUS_COLORS = {
  PENDING:     { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)' },
  DOWNLOADING: { color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.15)' },
  VERIFIED:    { color: '#a855f7', bg: 'rgba(168, 85, 247, 0.15)' },
  INSTALLED:   { color: '#22c55e', bg: 'rgba(34, 197, 94, 0.15)' },
  FAILED:      { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.15)' },
};

function CopyButton({ value, label }) {
  const [copied, setCopied] = useState(false);
  const onCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={onCopy}
      className="btn btn-secondary btn-sm"
      title={'Copy ' + (label || 'value')}
      style={{ padding: '4px 8px' }}
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
    </button>
  );
}

const EntryApkDetail = () => {
  const { apkId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [apk, setApk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const fetchApk = useCallback(async () => {
    try {
      const res = await fetch('/api/platform/entry/apks/' + apkId, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setApk(data.apk);
      } else if (res.status === 404) {
        setApk(null);
      }
    } catch (e) {
      console.error('Fetch APK error:', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [apkId]);

  useEffect(() => { fetchApk(); }, [fetchApk]);

  // Auto-refresh every 10s while there are in-flight pushes (non-terminal status)
  useEffect(() => {
    if (!apk) return;
    const inFlight = (apk.pushes || []).some(p =>
      ['PENDING', 'DOWNLOADING', 'VERIFIED'].includes(p.status)
    );
    if (!inFlight) return;
    const t = setInterval(() => fetchApk(), 10000);
    return () => clearInterval(t);
  }, [apk, fetchApk]);

  const handleDelete = async () => {
    try {
      const res = await fetch('/api/platform/entry/apks/' + apkId, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        navigate('/settings/entry/apks');
      } else {
        alert('Failed to delete APK');
      }
    } catch (e) {
      console.error(e);
    }
  };

  if (!user?.isPlatformAdmin) {
    return (
      <div className="entry-detail">
        <div className="entry-detail-empty">
          <AlertTriangle size={32} />
          <h3>Platform Admin Required</h3>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="entry-detail">
        <div className="entry-detail-loading">
          <RefreshCw size={24} className="spin" /><span>Loading APK...</span>
        </div>
      </div>
    );
  }

  if (!apk) {
    return (
      <div className="entry-detail">
        <div className="entry-detail-empty">
          <AlertTriangle size={32} />
          <h3>APK Not Found</h3>
          <button className="btn btn-secondary" onClick={() => navigate('/settings/entry/apks')}>
            <ArrowLeft size={16} /> Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="entry-detail">
      <div className="entry-detail-header">
        <div className="entry-detail-header-left">
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/settings/entry/apks')}>
            <ArrowLeft size={16} />
          </button>
          <div>
            <h1><Package size={22} /> {apk.version}</h1>
            <div className="entry-detail-subtitle">
              <span>versionCode {apk.versionCode}</span>
              <span style={{
                padding: '2px 8px', borderRadius: '4px', fontSize: '11px',
                textTransform: 'uppercase', letterSpacing: '0.3px',
                background: apk.buildType === 'release' ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.15)',
                color: apk.buildType === 'release' ? '#22c55e' : '#f59e0b'
              }}>{apk.buildType}</span>
              {!apk.isActive && (
                <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '11px', background: 'rgba(107,114,128,0.2)', color: '#9ca3af' }}>
                  Inactive
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="entry-detail-header-right">
          <button className="btn btn-secondary btn-sm" onClick={() => { setRefreshing(true); fetchApk(); }} disabled={refreshing}>
            <RefreshCw size={14} className={refreshing ? 'spin' : ''} />
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowEdit(true)}>
            <Edit3 size={14} /> Edit
          </button>
          {apk.isActive && (
            <button className="btn btn-danger btn-sm" onClick={() => setShowDelete(true)}>
              <Trash2 size={14} /> Soft Delete
            </button>
          )}
        </div>
      </div>

      <div className="entry-detail-section">
        {/* Metadata */}
        <div className="entry-detail-grid">
          <div className="entry-info-card">
            <h3><Hash size={16} /> Identity</h3>
            <div className="entry-info-rows">
              <div className="entry-info-row">
                <span className="entry-info-label">Version</span>
                <span className="entry-info-value">{apk.version}</span>
              </div>
              <div className="entry-info-row">
                <span className="entry-info-label">Version Code</span>
                <span className="entry-info-value">{apk.versionCode}</span>
              </div>
              <div className="entry-info-row">
                <span className="entry-info-label">Build Type</span>
                <span className="entry-info-value">{apk.buildType}</span>
              </div>
              <div className="entry-info-row">
                <span className="entry-info-label">Filename</span>
                <span className="entry-info-value mono" style={{ fontSize: '12px' }}>{apk.filename}</span>
              </div>
            </div>
          </div>

          <div className="entry-info-card">
            <h3><HardDrive size={16} /> Artifact</h3>
            <div className="entry-info-rows">
              <div className="entry-info-row">
                <span className="entry-info-label">Size</span>
                <span className="entry-info-value">{formatBytes(apk.sizeBytes)}</span>
              </div>
              <div className="entry-info-row" style={{ alignItems: 'flex-start' }}>
                <span className="entry-info-label">SHA-256</span>
                <span className="entry-info-value mono" style={{ fontSize: '11px', wordBreak: 'break-all', textAlign: 'right', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {apk.sha256}
                  <CopyButton value={apk.sha256} label="SHA-256" />
                </span>
              </div>
              <div className="entry-info-row" style={{ alignItems: 'flex-start' }}>
                <span className="entry-info-label">Download URL</span>
                <span className="entry-info-value mono" style={{ fontSize: '11px', wordBreak: 'break-all', textAlign: 'right', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <a href={apk.downloadUrl} target="_blank" rel="noreferrer" style={{ color: '#00d4aa' }}>{apk.downloadUrl}</a>
                  <CopyButton value={apk.downloadUrl} label="URL" />
                </span>
              </div>
            </div>
          </div>

          <div className="entry-info-card">
            <h3><UserIcon size={16} /> Provenance</h3>
            <div className="entry-info-rows">
              <div className="entry-info-row">
                <span className="entry-info-label">Uploaded</span>
                <span className="entry-info-value">{formatDate(apk.uploadedAt)}</span>
              </div>
              <div className="entry-info-row">
                <span className="entry-info-label">Uploaded By</span>
                <span className="entry-info-value">{apk.uploader?.name || apk.uploader?.email || '—'}</span>
              </div>
              <div className="entry-info-row">
                <span className="entry-info-label"><GitBranch size={12} style={{ marginRight: '4px' }} />Git Commit</span>
                <span className="entry-info-value mono" style={{ fontSize: '12px' }}>{apk.gitCommit || '—'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Changelog */}
        <div className="entry-info-card" style={{ marginTop: '16px' }}>
          <h3><FileText size={16} /> Changelog</h3>
          {apk.changelog ? (
            <pre style={{
              whiteSpace: 'pre-wrap',
              fontFamily: 'ui-monospace, monospace',
              color: 'var(--text-secondary)',
              fontSize: '12px',
              background: 'rgba(0,0,0,0.2)',
              padding: '12px',
              borderRadius: '6px',
              margin: 0,
            }}>{apk.changelog}</pre>
          ) : (
            <p style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>No changelog provided.</p>
          )}
        </div>

        {/* Push history */}
        <div className="entry-info-card" style={{ marginTop: '16px' }}>
          <h3><Download size={16} /> Push History ({(apk.pushes || []).length})</h3>
          {(apk.pushes || []).length === 0 ? (
            <p style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>
              This APK has not been pushed to any device yet.
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left' }}>
                    <th style={thStyle}>Device</th>
                    <th style={thStyle}>Initiated</th>
                    <th style={thStyle}>By</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Completed</th>
                    <th style={thStyle}>Failure</th>
                  </tr>
                </thead>
                <tbody>
                  {apk.pushes.map(push => {
                    const s = PUSH_STATUS_COLORS[push.status] || PUSH_STATUS_COLORS.PENDING;
                    return (
                      <tr key={push.id} style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                        <td style={tdStyle}>
                          <a href={'/entry/device/' + push.device?.id} style={{ color: '#00d4aa' }}>
                            {push.device?.name || push.deviceId}
                          </a>
                        </td>
                        <td style={tdStyle}>{formatDate(push.initiatedAt)}</td>
                        <td style={tdStyle}>{push.initiator?.name || push.initiator?.email || '—'}</td>
                        <td style={tdStyle}>
                          <span style={{
                            padding: '2px 8px', borderRadius: '4px', fontSize: '11px',
                            color: s.color, background: s.bg,
                          }}>{push.status}</span>
                        </td>
                        <td style={tdStyle}>{push.completedAt ? formatDate(push.completedAt) : '—'}</td>
                        <td style={{ ...tdStyle, color: '#ef4444', fontSize: '11px', maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {push.failureReason || '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showEdit && (
        <EditModal
          apk={apk}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); fetchApk(); }}
        />
      )}

      {showDelete && (
        <div className="platform-modal-overlay" onClick={() => setShowDelete(false)}>
          <div className="platform-modal delete" onClick={e => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <div className="platform-modal-header">
              <h3><AlertTriangle size={18} className="icon-danger" /> Soft Delete APK</h3>
              <button onClick={() => setShowDelete(false)}><X size={18} /></button>
            </div>
            <div className="platform-modal-body">
              <p className="platform-delete-message">
                This will mark <strong>{apk.version}</strong> as inactive and hide it from the
                APK picker. Push history is preserved. The file remains on disk and can be
                re-activated later by editing.
              </p>
            </div>
            <div className="platform-modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDelete(false)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete}>Soft Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function EditModal({ apk, onClose, onSaved }) {
  const [changelog, setChangelog] = useState(apk.changelog || '');
  const [isActive, setIsActive] = useState(!!apk.isActive);
  const [gitCommit, setGitCommit] = useState(apk.gitCommit || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/platform/entry/apks/' + apk.id, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ changelog, isActive, gitCommit }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Save failed');
        setSaving(false);
        return;
      }
      onSaved();
    } catch (e) {
      setError(e.message || 'Network error');
      setSaving(false);
    }
  };

  return (
    <div className="platform-modal-overlay" onClick={onClose}>
      <div className="platform-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '560px' }}>
        <div className="platform-modal-header">
          <h3><Edit3 size={18} /> Edit APK</h3>
          <button onClick={onClose} disabled={saving}><X size={18} /></button>
        </div>
        <div className="platform-modal-body" style={{ display: 'grid', gap: '14px' }}>
          {error && (
            <div style={{ background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '6px', padding: '10px 12px', color: '#ef4444', fontSize: '13px' }}>
              {error}
            </div>
          )}
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '6px' }}>Changelog</label>
            <textarea
              rows={6}
              value={changelog}
              onChange={e => setChangelog(e.target.value)}
              disabled={saving}
              style={{ ...editInputStyle, fontFamily: 'ui-monospace, monospace', resize: 'vertical' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '6px' }}>Git Commit</label>
            <input
              type="text"
              value={gitCommit}
              onChange={e => setGitCommit(e.target.value)}
              disabled={saving}
              style={editInputStyle}
            />
          </div>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: 'var(--text-secondary)', fontSize: '13px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={isActive}
              onChange={e => setIsActive(e.target.checked)}
              disabled={saving}
            />
            Active (visible in APK picker)
          </label>
        </div>
        <div className="platform-modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? <><RefreshCw size={14} className="spin" /> Saving...</> : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

const thStyle = {
  padding: '8px 10px',
  fontSize: '11px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: 'var(--text-tertiary)',
  borderBottom: '1px solid rgba(255,255,255,0.08)',
};
const tdStyle = {
  padding: '10px',
  fontSize: '13px',
  color: 'var(--text-secondary)',
};
const editInputStyle = {
  width: '100%',
  background: '#151a23',
  color: 'var(--text-primary)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: '6px',
  padding: '8px 12px',
  fontSize: '13px',
};

export default EntryApkDetail;
